import type {
  EstimateItemRequest,
  EstimatePartyInformation,
  EstimatePricing,
  EstimateProductRequest,
  EstimateProductResponse,
  EstimateRatingRequest,
  EstimateServicePointFlagPair,
  EstimateServiceRequest,
} from '../../../../core/models/estimate-rating';
import type {
  EstimateManagerDeliveryDateOption,
  EstimateManagerPackageItem,
  EstimateManagerPricing,
  EstimateManagerProductResponse,
  EstimateManagerRatingInput,
} from '../../../../core/models/estimates-orchestrator';
import type {
  ServiceAvailabilityProduct,
  ServiceAvailabilityResponse,
} from '../../../../core/models/service-availability';
import type { AddressFormValue } from '../../../ship-from-ship-to/models/address-form.model';
import {
  expressPackDefaultWeightForUnit,
  toFiniteNumber,
  type PackagePieceRowValue,
  type PackageSelectionFormValue,
} from '../../../package-selection/forms/package-selection.form';
import {
  evaluateCustomerSpecialHandling,
  type CustomerSpecialHandlingType,
  weightToPounds,
} from '../../../package-selection/customer-special-handling';
import {
  isExpressCataloguePackage,
  isExpressPackOrBox,
  type PackageUnitSystem,
} from '../../../package-selection/package-types.config';
import { environment } from '../../../../../environments/environment';

/** Signature requirement codes accepted from the Service step form (S4 RES / ZS50). */
export type OrchestratorSignatureRequirement = 'none' | 'RES' | 'ZS50';

/** Accessorial intent flags sent to the estimates orchestrator (backend maps to RES/ZS50/COV). */
export interface OrchestratorAccessorialFlags {
  hasResidentialSignature?: boolean;
  hasAdultSignature?: boolean;
  declaredValue?: number | null;
}

/**
 * Package special-handling types → estimate service codes (shipping label SH05–SH10).
 * SH10 (Residential Area Heavyweight) also requires destination residential + low-density.
 */
const SPECIAL_HANDLING_SERVICE_CODES: Record<CustomerSpecialHandlingType, string> = {
  flatPackage: 'SH05',
  additionalHandling: 'SH06',
  largePackage: 'SH07',
  overMaximumLimit: 'SH08',
  oversized: 'SH09',
  residentialHeavyWeight: 'SH10',
};

/** Stable emission order for piece-level estimate `services` (SH05–SH10). */
const SPECIAL_HANDLING_SERVICE_ORDER = ['SH05', 'SH06', 'SH07', 'SH08', 'SH09', 'SH10'] as const;

/** Weight threshold (lb) for Residential Area Heavyweight (SH10). */
const RESIDENTIAL_HEAVYWEIGHT_LB = 70;

/** Destination flags used when evaluating Residential Area Heavyweight (SH10). */
export interface EstimateSpecialHandlingOptions {
  destinationResidential?: boolean;
  destinationLowDensity?: boolean;
}

/**
 * Known local/dev demo account id. Prefer {@link resolveServiceStepAccountNumber} —
 * production and staging must not fall back to this constant.
 */
export const SERVICE_STEP_DEMO_ACCOUNT = '6968406';

/** LOB code for domestic courier PSA / estimate calls. */
export const SERVICE_STEP_LOB_CODE = 'PINC';

/**
 * Courier product codes from Product Master package-selection mocks
 * (`package-selection.mock.json`), unique and in fixture order.
 * Used when package selection does not return productCodes.
 */
export const COURIER_PRODUCT_CODE_CATALOG: readonly string[] = [
  '200',
  '201',
  '202',
  '203',
  '204',
  '206',
  '260',
  '261',
  '263',
  '264',
  '266',
  '210',
  '211',
  '212',
  '213',
  '214',
  '215',
  '216',
  '217',
  '218',
  '219',
  '220',
  '221',
  '222',
  '223',
  '224',
];

/**
 * Resolves the PSA/estimate account: explicit account wins; otherwise
 * {@link environment.standardAccountNumber}.
 */
export function resolveServiceStepAccountNumber(accountNumber?: string): string {
  const explicit = accountNumber?.trim();
  if (explicit) {
    return explicit;
  }
  return environment.standardAccountNumber?.trim() ?? '';
}

/**
 * Reads a boolean LDD flag from an ESD PSA payload using common field name variants.
 */
function readLddFlag(
  source: Record<string, unknown> | undefined,
  keys: string[],
): boolean | undefined {
  if (!source) {
    return undefined;
  }
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return undefined;
}

/**
 * Extracts origin/destination low-density flags from a PSA (service-availability) response.
 * ESN parity: AvailableProducts response exposes destinationLdd / originLdd for SH10 and LDD.
 */
export function resolveLowDensityFlagsFromPsa(
  response: ServiceAvailabilityResponse | null | undefined,
): EstimateServicePointFlagPair | undefined {
  if (!response) {
    return undefined;
  }

  const pair = response.senderReceiverPairs?.[0] as Record<string, unknown> | undefined;
  const root = response as Record<string, unknown>;
  const origin =
    readLddFlag(root, ['originLdd', 'originLDD', 'isOriginLdd']) ??
    readLddFlag(pair, ['originLdd', 'originLDD', 'isOriginLdd']);
  const destination =
    readLddFlag(root, ['destinationLdd', 'destinationLDD', 'isDestinationLdd']) ??
    readLddFlag(pair, ['destinationLdd', 'destinationLDD', 'isDestinationLdd']);

  if (origin === undefined && destination === undefined) {
    return undefined;
  }

  return {
    ...(origin !== undefined ? { origin } : {}),
    ...(destination !== undefined ? { destination } : {}),
  };
}

/**
 * Formats a Date as YYYY/MM/DD for estimate rating requests.
 */
export function formatShipmentDateForEstimate(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

/**
 * Formats a Date as YYYY-MM-DD for PSA shipDate.
 */
export function formatShipmentDateForPsa(date: Date = new Date()): string {
  return formatShipmentDateForEstimate(date).replaceAll('/', '-');
}

/**
 * Maps a Ship From / Ship To address form value to estimate party fields.
 */
export function mapAddressToEstimateParty(address: AddressFormValue): EstimatePartyInformation {
  return {
    customerName: address.companyOrFullName || undefined,
    streetAddress1: address.streetAddress || undefined,
    cityName: address.city || undefined,
    province: address.province ?? undefined,
    postalCode: address.postalCode || undefined,
    countryCode: 'CA',
  };
}

/**
 * Orders and maps special-handling codes to estimate service request entries.
 */
function toOrderedEstimateServices(codes: Set<string>): EstimateServiceRequest[] {
  return SPECIAL_HANDLING_SERVICE_ORDER.filter((code) => codes.has(code)).map((serviceCode) => ({
    serviceCode,
  }));
}

/**
 * Maps one customer packaging piece to piece-level estimate service codes SH05–SH10.
 * Flat→SH05, Additional→SH06, Large→SH07, Over Max→SH08, Oversized→SH09,
 * Residential Area Heavyweight→SH10 (weight > 70 lb + residential low-density destination).
 */
export function mapPieceToEstimateServices(
  piece: PackagePieceRowValue,
  unitSystem: PackageUnitSystem,
  options?: EstimateSpecialHandlingOptions,
): EstimateServiceRequest[] {
  const codes = new Set<string>();
  const types = evaluateCustomerSpecialHandling(
    {
      identicalPieces: piece.identicalPieces,
      perPieceWeight: piece.perPieceWeight,
      length: piece.length,
      width: piece.width,
      height: piece.height,
      requiresAdditionalHandling: piece.requiresAdditionalHandling,
    },
    unitSystem,
  );

  for (const type of types) {
    const serviceCode = SPECIAL_HANDLING_SERVICE_CODES[type];
    if (serviceCode) {
      codes.add(serviceCode);
    }
  }

  const weightLb = weightToPounds(piece.perPieceWeight ?? 0, unitSystem);
  if (
    weightLb > RESIDENTIAL_HEAVYWEIGHT_LB &&
    options?.destinationResidential === true &&
    options?.destinationLowDensity === true
  ) {
    codes.add('SH10');
  }

  return toOrderedEstimateServices(codes);
}

/**
 * Maps Express catalogue special handling to the single estimate item's services list.
 */
export function mapExpressToEstimateServices(
  packageValue: PackageSelectionFormValue,
  options?: EstimateSpecialHandlingOptions,
): EstimateServiceRequest[] {
  const codes = new Set<string>();
  if (packageValue.expressRequiresAdditionalHandling) {
    codes.add('SH06');
  }
  const expressWeightLb = weightToPounds(
    packageValue.expressTotalWeight ?? 0,
    packageValue.unitSystem,
  );
  if (
    expressWeightLb > RESIDENTIAL_HEAVYWEIGHT_LB &&
    options?.destinationResidential === true &&
    options?.destinationLowDensity === true
  ) {
    codes.add('SH10');
  }
  return toOrderedEstimateServices(codes);
}

/**
 * Counts pieces flagged for additional handling (AH checkbox), including Express Pack/Box.
 * Used by Estimated Costs `(Npc)` copy so two requested AH pieces still show `(2pc)` when
 * OM-138 skips SH06 on a piece that already won a higher special-handling code.
 *
 * @param packageValue Package-step form value
 */
export function countAdditionalHandlingPieces(packageValue: PackageSelectionFormValue): number {
  if (isExpressPackOrBox(packageValue.packageTypeId)) {
    return packageValue.expressRequiresAdditionalHandling ? 1 : 0;
  }

  return packageValue.pieces.reduce((sum, piece) => {
    if (!piece.requiresAdditionalHandling) {
      return sum;
    }

    const count = toFiniteNumber(piece.identicalPieces);
    return sum + Math.max(count, 1);
  }, 0);
}

/**
 * Counts pieces that carry each special-handling service code (SH05–SH10).
 * Used by the Estimated Costs panel for typed detail copy such as "Large package (1pc)".
 * SH06 uses the additional-handling checkbox count so display matches requested pieces.
 */
export function countSpecialHandlingPiecesByCode(
  packageValue: PackageSelectionFormValue,
  options?: EstimateSpecialHandlingOptions,
): Record<string, number> {
  const counts: Record<string, number> = {};

  const addCodes = (services: EstimateServiceRequest[], pieceCount: number): void => {
    const quantity = Math.max(1, pieceCount);
    for (const service of services) {
      const code = (service.serviceCode ?? '').trim().toUpperCase();
      if (!code) {
        continue;
      }
      counts[code] = (counts[code] ?? 0) + quantity;
    }
  };

  if (isExpressCataloguePackage(packageValue.packageTypeId)) {
    addCodes(mapExpressToEstimateServices(packageValue, options), 1);
    return counts;
  }

  for (const piece of packageValue.pieces) {
    addCodes(
      mapPieceToEstimateServices(piece, packageValue.unitSystem, options),
      toFiniteNumber(piece.identicalPieces) || 1,
    );
  }

  const additionalHandlingCount = countAdditionalHandlingPieces(packageValue);
  if (additionalHandlingCount > 0) {
    counts['SH06'] = additionalHandlingCount;
  }

  return counts;
}

/**
 * Sums `identicalPieces` per special-handling code across the backend-rated `ratingInput.items`.
 * These are the codes Estimate Manager actually rated, so they can differ from the wizard recount.
 *
 * @returns Counts keyed by upper-case service code, or `undefined` when no item carries a code
 */
export function countRatedSpecialHandlingPieces(
  ratingInput: EstimateManagerRatingInput | undefined,
): Record<string, number> | undefined {
  const counts: Record<string, number> = {};

  for (const item of ratingInput?.items ?? []) {
    const quantity = Math.max(1, toFiniteNumber(item.identicalPieces));
    for (const service of item.services ?? []) {
      const code = (service.serviceCode ?? '').trim().toUpperCase();
      if (code) {
        counts[code] = (counts[code] ?? 0) + quantity;
      }
    }
  }

  return Object.keys(counts).length > 0 ? counts : undefined;
}

/**
 * Prefers backend-rated special-handling piece counts over the wizard recount (OM-885).
 * Estimate Manager applies OM-138 hierarchy (+ LDD for SH10); the wizard cannot, so rated
 * counts are authoritative for SH05–SH10 when present. Wizard SH06 is kept when rated omits
 * it so Additional Handling `(Npc)` still matches the checkbox request.
 *
 * @param wizardCounts Counts from {@link countSpecialHandlingPiecesByCode}
 * @param ratedCounts Counts from {@link countRatedSpecialHandlingPieces}
 */
export function applyRatedSpecialHandlingPieceCounts(
  wizardCounts: Record<string, number> | undefined,
  ratedCounts: Record<string, number> | undefined,
): Record<string, number> | undefined {
  if (!ratedCounts) {
    return wizardCounts;
  }

  const merged: Record<string, number> = { ...ratedCounts };
  const wizardAdditionalHandling = wizardCounts?.['SH06'];
  if (wizardAdditionalHandling !== undefined && merged['SH06'] === undefined) {
    merged['SH06'] = wizardAdditionalHandling;
  }

  return merged;
}

/**
 * Builds package piece items for the estimate request from package step state.
 * Express catalogue types (`1`/`2`/`4`) use total weight only; Customer Packaging (`3`) maps each piece row.
 * Special handling (SH05–SH10) is attached on each item's `services` list (ESN/S4 item SERVICES).
 * Coerces form string values with `toFiniteNumber` so `.toFixed` never throws before the API call.
 */
export function mapPackageToEstimateItems(
  packageValue: PackageSelectionFormValue,
  options?: EstimateSpecialHandlingOptions,
): EstimateItemRequest[] {
  if (isExpressCataloguePackage(packageValue.packageTypeId)) {
    // Express Envelope has no weight input, so it always falls back to the default; use the
    // unit-aware value (OM-714) so metric shipments don't send an inflated raw `1` (~1 kg).
    const weight =
      toFiniteNumber(packageValue.expressTotalWeight) ||
      expressPackDefaultWeightForUnit(packageValue.unitSystem);
    const services = mapExpressToEstimateServices(packageValue, options);
    return [
      {
        pieceCountPerUnit: '1',
        itemId: '1',
        declaredWeight: weight.toFixed(2),
        ...(services.length > 0 ? { services } : {}),
      },
    ];
  }

  if (packageValue.pieces.length === 0) {
    return [
      {
        pieceCountPerUnit: '1',
        itemId: '1',
        declaredWeight: '1.00',
      },
    ];
  }

  return packageValue.pieces.map((piece, index) => {
    const weight = toFiniteNumber(piece.perPieceWeight) || 1;
    const length = toFiniteNumber(piece.length);
    const width = toFiniteNumber(piece.width);
    const height = toFiniteNumber(piece.height);
    const services = mapPieceToEstimateServices(piece, packageValue.unitSystem, options);

    return {
      pieceCountPerUnit: String(toFiniteNumber(piece.identicalPieces) || 1),
      itemId: String(index + 1),
      declaredWeight: weight.toFixed(2),
      declaredLength: length > 0 ? length : undefined,
      declaredWidth: width > 0 ? width : undefined,
      declaredHeight: height > 0 ? height : undefined,
      ...(services.length > 0 ? { services } : {}),
    };
  });
}

/**
 * Maps PSA availableProducts into estimate product selections.
 */
export function mapPsaProductsToEstimateProducts(
  products: ServiceAvailabilityProduct[] | null | undefined,
): EstimateProductRequest[] {
  if (!products?.length) {
    return [];
  }

  return products
    .filter((product): product is ServiceAvailabilityProduct => product != null)
    .map((product) => ({
      productNumber: String(product.productNumber).trim(),
      transportationMode: String(product.transportationMode ?? 'A').trim() || 'A',
    }))
    .filter((product) => product.productNumber.length > 0);
}

/**
 * Builds an estimate rating request from addresses, package, and PSA products.
 */
export function buildEstimateRatingRequest(input: {
  shipFrom: AddressFormValue;
  shipTo: AddressFormValue;
  packageValue: PackageSelectionFormValue;
  products: EstimateProductRequest[];
  accountNumber?: string;
  shipDate?: Date;
  /**
   * Origin/destination low-density flags from PSA (AVS/address serviceability path).
   * Drives estimate `lowDensity` and Residential Area Heavyweight (SH10) on items.
   */
  lowDensity?: EstimateServicePointFlagPair;
  /** @deprecated Prefer `lowDensity.destination` — kept for call-site clarity. */
  destinationLowDensity?: boolean;
}): EstimateRatingRequest {
  const destinationLowDensity =
    input.destinationLowDensity === true || input.lowDensity?.destination === true;
  const lowDensity: EstimateServicePointFlagPair | undefined =
    input.lowDensity ?? (input.destinationLowDensity === true ? { destination: true } : undefined);
  const shipmentUoms = mapPackageUnitSystemToShipmentUoms(input.packageValue.unitSystem);

  return {
    shipmentId: `SHIP-${Date.now()}`,
    shipmentDate: formatShipmentDateForEstimate(input.shipDate),
    accountNumber: resolveServiceStepAccountNumber(input.accountNumber),
    lobCode: SERVICE_STEP_LOB_CODE,
    ...shipmentUoms,
    originZoneCode: 'ORIGIN-ZONE',
    prInd: 'P2P',
    products: input.products,
    senderInformation: mapAddressToEstimateParty(input.shipFrom),
    receiverInformation: mapAddressToEstimateParty(input.shipTo),
    ...(lowDensity ? { lowDensity } : {}),
    items: mapPackageToEstimateItems(input.packageValue, {
      destinationResidential: input.shipTo.residential,
      destinationLowDensity,
    }),
    services: [],
  };
}

/**
 * Resolves product codes for Product Master product selection without PSA.
 * Prefers codes returned on package-selection rows; falls back to the courier catalog.
 */
export function resolveCourierProductCodes(packages: { productCodes?: string[] }[]): string[] {
  const fromPackages = [
    ...new Set(
      packages.flatMap((item) => item.productCodes ?? []).filter((code) => code.trim().length > 0),
    ),
  ];
  return fromPackages.length > 0 ? fromPackages : [...COURIER_PRODUCT_CODE_CATALOG];
}

/**
 * Builds orchestrator accessorial flags from signature and declared-value selections.
 * Backend maps hasResidentialSignature → RES, hasAdultSignature → ZS50,
 * neither signature flag → OSNR, and declaredValue → COV.
 */
export function buildOrchestratorAccessorialFlags(input: {
  signatureRequirement?: OrchestratorSignatureRequirement | null;
  declaredValue?: number | null;
}): OrchestratorAccessorialFlags {
  const flags: OrchestratorAccessorialFlags = {};
  const requirement = input.signatureRequirement ?? 'none';
  if (requirement === 'RES') {
    flags.hasResidentialSignature = true;
  } else if (requirement === 'ZS50') {
    flags.hasAdultSignature = true;
  }

  const declaredValue = input.declaredValue;
  if (typeof declaredValue === 'number' && Number.isFinite(declaredValue) && declaredValue > 0) {
    flags.declaredValue = declaredValue;
  }

  return flags;
}

/**
 * Maps Package step unit system to Estimate Manager shipment UOM codes.
 */
export function mapPackageUnitSystemToShipmentUoms(unitSystem: PackageUnitSystem): {
  shipmentWeightUom: 'LB' | 'KG';
  shipmentDimensionUom: 'IN' | 'CM';
} {
  return {
    shipmentWeightUom: unitSystem === 'metric' ? 'KG' : 'LB',
    shipmentDimensionUom: unitSystem === 'metric' ? 'CM' : 'IN',
  };
}

/**
 * Maps Package step state into Estimate Manager package rows and required shipment UOMs.
 */
export function buildEstimateManagerPackagePayload(packageValue: PackageSelectionFormValue): {
  packages: EstimateManagerPackageItem[];
  shipmentWeightUom: 'LB' | 'KG';
  shipmentDimensionUom: 'IN' | 'CM';
} {
  return {
    packages: mapPackageToEstimateManagerItems(packageValue),
    ...mapPackageUnitSystemToShipmentUoms(packageValue.unitSystem),
  };
}

/**
 * Builds one Estimate Manager package piece from weight and dimensions.
 */
function toEstimateManagerPackageItem(input: {
  identicalPieces: number;
  perPieceWeight: number;
  length: number;
  width: number;
  height: number;
  requiresAdditionalHandling?: boolean;
}): EstimateManagerPackageItem {
  return {
    identicalPieces: input.identicalPieces,
    perPieceWeight: input.perPieceWeight,
    length: input.length,
    width: input.width,
    height: input.height,
    ...(input.requiresAdditionalHandling === true ? { requiresAdditionalHandling: true } : {}),
  };
}

/**
 * Maps package-step state into Estimate Manager `packages` rows.
 * Special-handling codes are derived server-side; do not attach SH services here.
 * Express catalogue types ignore stale Customer Packaging piece dimensions.
 */
export function mapPackageToEstimateManagerItems(
  packageValue: PackageSelectionFormValue,
): EstimateManagerPackageItem[] {
  if (isExpressCataloguePackage(packageValue.packageTypeId)) {
    return [
      toEstimateManagerPackageItem({
        identicalPieces: 1,
        // Express Envelope has no weight input, so it always falls back to the default; use the
        // unit-aware value (OM-714) so metric shipments don't send an inflated raw `1` (~1 kg).
        perPieceWeight:
          toFiniteNumber(packageValue.expressTotalWeight) ||
          expressPackDefaultWeightForUnit(packageValue.unitSystem),
        length: 0,
        width: 0,
        height: 0,
        requiresAdditionalHandling: packageValue.expressRequiresAdditionalHandling,
      }),
    ];
  }

  if (packageValue.pieces.length === 0) {
    return [
      toEstimateManagerPackageItem({
        identicalPieces: 1,
        perPieceWeight: 1,
        length: 0,
        width: 0,
        height: 0,
      }),
    ];
  }

  return packageValue.pieces.map((piece) =>
    toEstimateManagerPackageItem({
      identicalPieces: toFiniteNumber(piece.identicalPieces) || 1,
      perPieceWeight: toFiniteNumber(piece.perPieceWeight) || 1,
      length: toFiniteNumber(piece.length),
      width: toFiniteNumber(piece.width),
      height: toFiniteNumber(piece.height),
      requiresAdditionalHandling: piece.requiresAdditionalHandling,
    }),
  );
}

/**
 * Copies Estimate Manager string pricing into the EstimatePricing shape the matrix uses.
 * Preserves peakSurcharges so Estimated Costs can split Peak Large / Peak Oversize (OM-724).
 */
export function mapEstimateManagerPricingToEstimatePricing(
  pricing: EstimateManagerPricing | null | undefined,
): EstimatePricing | null {
  if (!pricing) {
    return null;
  }

  const peakSurcharges = (pricing.peakSurcharges ?? [])
    .filter((entry) => entry.productCode?.trim() && entry.amount?.trim())
    .map((entry) => ({
      productCode: entry.productCode.trim(),
      amount: entry.amount,
    }));

  return {
    basePrice: pricing.basePrice,
    totalPrice: pricing.totalPrice,
    fuelSurcharge: pricing.fuelSurcharge,
    taxes: { ...pricing.taxes },
    surcharges: { ...pricing.surcharges },
    ...(peakSurcharges.length > 0 ? { peakSurcharges } : {}),
    accessorialCharges: (pricing.accessorialCharges ?? [])
      .filter((entry) => entry.productCode?.trim() && entry.amount?.trim())
      .map((entry) => ({
        productCode: entry.productCode.trim(),
        amount: entry.amount,
      })),
  };
}

/**
 * Derives an approximate percent discount from full vs discounted totals, or coupon contractValue.
 */
function deriveDiscountRate(
  pricing: EstimatePricing,
  discountedPricing: EstimatePricing,
  contractValue?: string | null,
): number | undefined {
  const full = Number.parseFloat(pricing.totalPrice);
  const discounted = Number.parseFloat(discountedPricing.totalPrice);
  if (Number.isFinite(full) && Number.isFinite(discounted) && full > 0 && discounted < full) {
    return Math.round((1 - discounted / full) * 100);
  }

  const fromContract = Number.parseFloat(contractValue ?? '');
  if (Number.isFinite(fromContract) && fromContract > 0) {
    return Math.round(fromContract);
  }

  return undefined;
}

/** Estimate row enriched with optional backend-authoritative discount fields for the breakdown. */
export type OrchestratorMappedEstimate = EstimateProductResponse & {
  discountedPricing?: EstimatePricing;
  couponDiscount?: {
    discountRate: number;
    discountType: string;
  };
  attestation?: string;
  /** Piece counts per SH code from the backend-rated `ratingInput.items`. */
  ratedSpecialHandlingPieceCounts?: Record<string, number>;
};

/**
 * Maps one Estimate Manager product onto the PSA + estimate pair mergeDeliveryOfferings expects.
 * Returns null when pricing is missing (ESTIMATE_NOT_AVAILABLE / failed rating).
 */
function mapEstimateManagerProductToMergeRow(
  dateOption: EstimateManagerDeliveryDateOption,
  product: EstimateManagerProductResponse,
  estimateAttestation?: string,
): { psaProduct: ServiceAvailabilityProduct; estimate: OrchestratorMappedEstimate } | null {
  const productCode =
    product.product?.productCode?.trim() || product.serviceAvailability?.productCode?.trim();
  if (!productCode) {
    return null;
  }

  const pricing = mapEstimateManagerPricingToEstimatePricing(product.estimate?.pricing);
  if (!pricing) {
    return null;
  }

  const discountedPricing =
    product.coupon?.status === 'VALID'
      ? (mapEstimateManagerPricingToEstimatePricing(product.estimate?.discountedPricing) ??
        undefined)
      : undefined;

  const discountRate =
    discountedPricing !== undefined
      ? deriveDiscountRate(pricing, discountedPricing, product.coupon?.contractValue)
      : undefined;
  const ratedSpecialHandlingPieceCounts = countRatedSpecialHandlingPieces(product.ratingInput);

  return {
    psaProduct: {
      productNumber: productCode,
      transportationMode: product.serviceAvailability?.transportationMode?.trim() || 'A',
      expectedDeliveryDate:
        product.serviceAvailability?.expectedDeliveryDate || dateOption.expectedDeliveryDate,
    },
    estimate: {
      productCode,
      outcome: product.estimate?.outcome || 'RATING_SUCCESS',
      pricing,
      error: null,
      ...(discountedPricing ? { discountedPricing } : {}),
      ...(discountRate !== undefined
        ? {
            couponDiscount: {
              discountRate,
              discountType: '%',
            },
          }
        : {}),
      ...(estimateAttestation ? { attestation: estimateAttestation } : {}),
      ...(ratedSpecialHandlingPieceCounts ? { ratedSpecialHandlingPieceCounts } : {}),
    },
  };
}

/**
 * Maps Estimate Manager availableDeliveryDates into PSA + estimate inputs for mergeDeliveryOfferings.
 * Products without pricing are omitted from the matrix.
 */
export function mapOrchestratorDatesToMergeInput(
  dates: EstimateManagerDeliveryDateOption[],
  estimateAttestation?: string,
): {
  psaProducts: ServiceAvailabilityProduct[];
  estimates: OrchestratorMappedEstimate[];
} {
  const psaProducts: ServiceAvailabilityProduct[] = [];
  const estimates: OrchestratorMappedEstimate[] = [];

  for (const dateOption of dates) {
    for (const product of dateOption.products) {
      const mapped = mapEstimateManagerProductToMergeRow(dateOption, product, estimateAttestation);
      if (!mapped) {
        continue;
      }

      psaProducts.push(mapped.psaProduct);
      estimates.push(mapped.estimate);
    }
  }

  return { psaProducts, estimates };
}

/**
 * Collects non-empty product-selection JWTs keyed by product code.
 */
export function collectProductSelectionAttestations(
  products: { productCode: string; attestation?: string }[],
): Record<string, string> {
  const tokens: Record<string, string> = {};

  for (const product of products) {
    const code = product.productCode?.trim();
    const token = product.attestation?.trim();
    if (code && token) {
      tokens[code] = token;
    }
  }

  return tokens;
}

/**
 * Finds a priced product in an Estimate Manager response for the selected service.
 */
export function findEstimateManagerProduct(input: {
  dates: EstimateManagerDeliveryDateOption[];
  productCode: string;
  deliveryDate?: string;
  transportationMode?: string;
}): EstimateManagerProductResponse | undefined {
  const selectedDate = input.deliveryDate?.replaceAll('/', '-');
  const scopedDates =
    selectedDate !== undefined
      ? input.dates.filter(
          (option) => option.expectedDeliveryDate.replaceAll('/', '-') === selectedDate,
        )
      : input.dates;

  if (selectedDate !== undefined && scopedDates.length === 0) {
    return undefined;
  }

  const candidates = scopedDates.flatMap((option) => option.products);

  return candidates.find((candidate) => {
    const code =
      candidate.product?.productCode?.trim() || candidate.serviceAvailability?.productCode?.trim();
    if (code !== input.productCode) {
      return false;
    }
    const mode = candidate.serviceAvailability?.transportationMode;
    return !mode || !input.transportationMode || mode === input.transportationMode;
  });
}
